import { useEffect, useState } from "react";
import { GoSync } from "react-icons/go";
import { useTranslation } from "react-i18next";
import Dropdown from "../../components/Dropdown";
import Link from "../../components/Link";
import useYearOptions from "../../hooks/use-year-options";

import { useDispatch, useSelector } from "react-redux";
import Checkout from "../../components/Checkout";
import ImageShow from "../../components/ImageShow";
import InvestAccountContent from "../../components/InvestAccountContent";
import Modal from "../../components/Modal";
import Reports from "../../components/Reports";
import { setPay, setShowModal } from "../../store";
import { useLazyTaxDeclarationsInvestingAccountsQuery, useTaxDeclarationsYearQuery } from "../../store/apis/taxApi";
import { YearSection } from "./YearSection";

function InfoPage() {
  const dispatch = useDispatch();
  const { t } = useTranslation();

	const url = new URL(window.location.href);

  const paidParam = url.searchParams.get("paid");
  const yearParam = url.searchParams.get("year");
    
  const [mobileScreen, setMobileScreen] = useState(false);

  let { year, options } = useYearOptions();
	const [selection, setSelection] = useState({ label: yearParam || year, value: yearParam || year });

  const {
    data,
    currentData,
    error: errorTax,
    isFetching: isFetchingTax,
  } = useTaxDeclarationsYearQuery({ year: selection.value });
  // A refetch of the shown year (after the loss is saved) keeps the year on screen; only a year not loaded yet spins.
  const loadingTax = isFetchingTax && !currentData;
  const [
    triggerTaxDeclarationsInvestingAccounts, 
    { 
      error: errorInvestAccount, 
      data: dataInvestAccount, 
      isLoading: isFetchingInvestAccount 
    }
  ] = useLazyTaxDeclarationsInvestingAccountsQuery();

	const handleSelect = (option) => {
    setSelection(option);
  };

  const handleClose = () => {
    dispatch(setShowModal(false));
  };

  const allowCalculation =
    dataInvestAccount?.investingAccountForTaxDeclarations?.find((x) => x.hasPhantomOperations);

  const showModal = useSelector((state) => {
    return state.modal.showModal;
  });
  const paid = useSelector((state) => {
    return state.reports.paid;
  });

  useEffect(() => {
    if (!isFetchingTax && (errorTax || !data.yearData.data)) {
      triggerTaxDeclarationsInvestingAccounts({ year: selection.value });
    }
  }, [errorTax, isFetchingTax, data, triggerTaxDeclarationsInvestingAccounts, selection.value]);

  useEffect(() => {
    if (paid) {
      setTimeout(() => {
        dispatch(setPay(false));
        window.history.pushState({}, document.title, window.location.pathname);
      }, 4000);
    }
  }, [paid]);

  useEffect(() => {
    if (paidParam) {
      dispatch(setPay(true));
    }
  }, [paidParam, dispatch]);

  
  useEffect(() => {
    const handleResize = () => {
      setMobileScreen(window.innerWidth < 460);
    };
    window.addEventListener('resize', handleResize);

    handleResize();

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);
  
  if(mobileScreen) {
    return (
      <div className="page">
        <h1 className="text-black inline-block text-lg leading-10 sm:leading-normal sm:text-2xl sm:pb-1">
          {t('taxReports.title')}
        </h1>

        <div
            className="w-full items-center py-4 mt-4 sm:mt-16 min-h-[80px] 
          flex justify-left px-5 sm:px-7 border-transparent rounded-xl text-white 
          bg-gradient-to-r from-cyan-650 to-cyan-75">
            <p className="text-sm sm:text-normal">
              {t('taxReports.mobileWarning')}
            </p>
          </div>
      </div>
    )
  }
    
	return (
    <>
      <div className="page">
        <h1 className="text-black inline-block text-lg leading-10 sm:leading-normal sm:text-2xl sm:pb-1">
          {t('taxReports.title')}
        </h1>
        <div className="w-full sm:w-auto flex items-end flex-wrap sm:flex-nowrap mt-5 sm:mt-0">
          <p className="mr-2 text-gray-350 w-full sm:w-auto">
            {t('taxReports.selectPeriod')}
          </p>
          <Dropdown
            options={options}
            value={selection}
            onChange={handleSelect}
            noborder
            className="mt-1 sm:mt-0 text-2xl leading-7 bg-clip-text text-transparent bg-gradient-to-r from-cyan-650 to-cyan-75">
            <span>{year}</span>
          </Dropdown>
        </div>
        {((errorTax || !data?.yearData?.data) && (errorInvestAccount || !dataInvestAccount) && !loadingTax && !isFetchingInvestAccount) && (
          <div
            className="w-full items-center py-4 mt-7 sm:mt-16 min-h-[80px] 
          flex justify-left px-5 sm:px-7 border-transparent rounded-xl text-white 
          bg-gradient-to-r from-cyan-650 to-cyan-75">
            <p className="text-sm sm:text-normal">
              {t('taxReports.noReportsMessage')}{" "}
              <Link to="/reports" className="underline">
                "{t('taxReports.accountsLink')}"
              </Link>
            </p>
          </div>
        )}
        <div className="mt-7">
          {
            loadingTax || isFetchingInvestAccount
            ? <GoSync className="animate-spin inline-block text-green-700" />
            : !errorTax && data?.yearData?.data 
              ? <YearSection data={data} />
              : dataInvestAccount && !errorInvestAccount
                ? (
                  <InvestAccountContent
                    content={
                      <Reports
                        data={dataInvestAccount?.investingAccountForTaxDeclarations}
                        taxPage
                      />
                    }
                    allowCalculation={allowCalculation}
                  /> 
                ) : null
          }
        </div>
      </div>
      {paid &&  
        <Modal
          onClose={handleClose}
          actionBar={
            <>
              <ImageShow
                image={{ url: "checked-big-white", label: "checked big white" }}
              />
              <p className="mt-3 text-white">{t('taxReports.paymentSuccess')}</p>
            </>
          }
          paid
        />
      }
      {showModal &&  
        <Modal
          onClose={handleClose}
          actionBar={<Checkout year={selection.value} />}
          checkout
        />
      }
    </>
  );
}

export default InfoPage;